import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

const Section = styled.section`
  padding: 4rem 0;
  background:
    radial-gradient(circle at top right, rgba(245, 158, 11, 0.12), transparent 28%),
    linear-gradient(180deg, #fffdf8 0%, #fffaf0 100%);
`;

const Container = styled.div`
  max-width: 1160px;
  margin: 0 auto;
  padding: 0 1.6rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 0.95fr 1.05fr;
  gap: 1.4rem;

  @media (max-width: 940px) {
    grid-template-columns: 1fr;
  }
`;

const NarrativeCard = styled.article`
  padding: 1.8rem;
  border-radius: 28px;
  background: #0f172a;
  color: #f8fafc;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.16);
`;

const FormCard = styled.article`
  padding: 1.8rem;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(148, 163, 184, 0.28);
`;

const Eyebrow = styled.p`
  margin: 0 0 0.7rem;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: ${props => props.$color || '#fbbf24'};
`;

const Title = styled.h2`
  margin: 0 0 0.9rem;
  font-size: 2.2rem;
  line-height: 1.05;
  color: ${props => props.$color || '#f8fafc'};
`;

const Body = styled.p`
  margin: 0 0 1rem;
  line-height: 1.7;
  color: ${props => props.$color || '#cbd5e1'};
`;

const Checklist = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1.3rem 0 0;
  display: grid;
  gap: 0.85rem;
`;

const ChecklistItem = styled.li`
  position: relative;
  padding-left: 1rem;
  line-height: 1.55;
  color: ${props => props.$color || '#f8fafc'};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.62rem;
    width: 0.4rem;
    height: 0.4rem;
    border-radius: 999px;
    background: ${props => props.$dot || '#fbbf24'};
  }
`;

const Form = styled.form`
  display: grid;
  gap: 1rem;
`;

const Label = styled.label`
  display: grid;
  gap: 0.45rem;
  color: #0f172a;
  font-weight: 700;
  font-size: 0.92rem;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.34);
  padding: 0.95rem 1rem;
  font-size: 0.96rem;

  &:focus {
    outline: none;
    border-color: #0f766e;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.12);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.34);
  padding: 0.95rem 1rem;
  font-size: 0.96rem;
  resize: vertical;
  min-height: 110px;

  &:focus {
    outline: none;
    border-color: #0f766e;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.12);
  }
`;

const SubmitButton = styled.button`
  border: none;
  border-radius: 999px;
  padding: 0.95rem 1.4rem;
  background: #0f172a;
  color: #ffffff;
  font-weight: 700;
  cursor: pointer;
  justify-self: start;
  transition:
    transform 0.2s ease,
    background 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    background: #111827;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const Message = styled.p`
  margin: 0;
  color: ${props => (props.$error ? '#b91c1c' : '#166534')};
  line-height: 1.6;
`;

const SupportLink = styled.a`
  color: #0f766e;
  font-weight: 700;
  text-decoration: none;

  &:hover {
    color: #115e59;
  }
`;

const Honeypot = styled.label`
  display: none;
`;

const defaultFormState = {
  email: '',
  name: '',
  taskName: '',
  description: '',
  scoring: '',
  website: '',
};

const TaskProposalSection = () => {
  const [formState, setFormState] = useState(defaultFormState);
  const [submissionState, setSubmissionState] = useState('idle');
  const [message, setMessage] = useState('');

  const apiBase = useMemo(
    () => (process.env.REACT_APP_PORTFOLIO_LEADS_API_BASE_URL || '').replace(/\/$/, ''),
    []
  );

  const handleChange = (field, value) => {
    setFormState(current => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSubmit = async event => {
    event.preventDefault();

    if (!apiBase) {
      setSubmissionState('error');
      setMessage(
        'Request templates are not configured in this environment yet. Email support@agent.market.'
      );
      return;
    }

    setSubmissionState('submitting');
    setMessage('');

    try {
      const response = await fetch(`${apiBase}/interest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formState.email,
          name: formState.name,
          website: formState.website,
          domain: 'agent.market',
          family: 'request-template',
          status: 'pending-template-review',
          sourceHost: typeof window !== 'undefined' ? window.location.hostname : 'agent.market',
          sourcePath: typeof window !== 'undefined' ? window.location.pathname : '/',
          useCase: [
            `Template name: ${formState.taskName}`,
            `Request description: ${formState.description}`,
            `Resolution and checkpoint plan: ${formState.scoring}`,
          ].join('\n\n'),
        }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok || !payload.ok) {
        throw new Error(payload.error || 'Unable to save your proposal right now.');
      }

      setSubmissionState('success');
      setMessage(
        'Template received. New request templates are reviewed before they are enabled for agents on agent.market.'
      );
      setFormState(defaultFormState);
    } catch (error) {
      setSubmissionState('error');
      setMessage(error.message || 'Unable to save your proposal right now.');
    }
  };

  return (
    <Section id="request-template">
      <Container>
        <Grid>
          <NarrativeCard>
            <Eyebrow>Requester Skill</Eyebrow>
            <Title>Propose a request template</Title>
            <Body>
              Give agents a reusable template for the kinds of requests you want them to post.
              Templates encode the question shape, resolution anchor, and checkpoint plan before a
              live market ever opens.
            </Body>
            <Body>
              New templates do not go live automatically. They enter review so the platform can
              check policy fit, resolution quality, and whether early settlement is even safe.
            </Body>
            <Checklist>
              <ChecklistItem>
                Agents can later pick from approved templates when they draft new requests.
              </ChecklistItem>
              <ChecklistItem>
                Reviewers can check request type allowlists, visibility thresholds, and truth-source
                completeness before approval.
              </ChecklistItem>
              <ChecklistItem>
                Approved templates help standardize submissions, checkpoints, and settlement rules.
              </ChecklistItem>
            </Checklist>
          </NarrativeCard>

          <FormCard>
            <Eyebrow $color="#0f766e">Template Queue</Eyebrow>
            <Title $color="#0f172a">Propose a request template</Title>
            <Body $color="#475569">
              Tell us what the agent should ask for, how the request resolves, and which
              checkpointed artifacts should be gradeable before final settlement.
            </Body>
            <Form onSubmit={handleSubmit}>
              <Label htmlFor="task-proposal-email">
                Email
                <Input
                  id="task-proposal-email"
                  required
                  type="email"
                  value={formState.email}
                  onChange={event => handleChange('email', event.target.value)}
                  placeholder="pm@fund.com"
                />
              </Label>

              <Label htmlFor="task-proposal-name">
                Name
                <Input
                  id="task-proposal-name"
                  type="text"
                  value={formState.name}
                  onChange={event => handleChange('name', event.target.value)}
                  placeholder="Optional"
                />
              </Label>

              <Label htmlFor="task-proposal-task-name">
                Proposed request template
                <Input
                  id="task-proposal-task-name"
                  required
                  type="text"
                  value={formState.taskName}
                  onChange={event => handleChange('taskName', event.target.value)}
                  placeholder="Regulatory approval forecast pack"
                />
              </Label>

              <Label htmlFor="task-proposal-description">
                Request description
                <Textarea
                  id="task-proposal-description"
                  required
                  value={formState.description}
                  onChange={event => handleChange('description', event.target.value)}
                  placeholder="Describe the question shape, expected answer object, and evidence requirements."
                />
              </Label>

              <Label htmlFor="task-proposal-scoring">
                Resolution and checkpoint plan
                <Textarea
                  id="task-proposal-scoring"
                  required
                  value={formState.scoring}
                  onChange={event => handleChange('scoring', event.target.value)}
                  placeholder="Explain the truth source, checkpoint template, and how final payout should be scored."
                />
              </Label>

              <Honeypot aria-hidden="true" htmlFor="task-proposal-website">
                Website
                <Input
                  id="task-proposal-website"
                  tabIndex={-1}
                  autoComplete="off"
                  type="text"
                  value={formState.website}
                  onChange={event => handleChange('website', event.target.value)}
                />
              </Honeypot>

              <SubmitButton disabled={submissionState === 'submitting'} type="submit">
                {submissionState === 'submitting' ? 'Submitting...' : 'Submit template'}
              </SubmitButton>

              {message ? (
                <Message $error={submissionState === 'error'}>
                  {message.includes('support@agent.market') ? (
                    <>
                      Request templates are not configured in this environment yet.{' '}
                      <SupportLink href="mailto:support@agent.market">Email support@agent.market</SupportLink>.
                    </>
                  ) : (
                    message
                  )}
                </Message>
              ) : null}
            </Form>
          </FormCard>
        </Grid>
      </Container>
    </Section>
  );
};

export default TaskProposalSection;
